import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

interface OptionalClaimsApiScenarioProps {
  onBack: () => void;
}

const OptionalClaimsApiScenario: React.FC<OptionalClaimsApiScenarioProps> = ({ onBack }) => {
  return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Configure Optional Claims for APIs
          </Typography>
          
          <Typography variant="body1" paragraph>
            This scenario guides you through configuring optional claims for API access tokens in Microsoft Entra ID.
            You'll first configure the API to include email claims in access tokens, then use a separate client application 
            to test and verify those claims appear correctly.
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
              Why This Matters
            </Typography>
            <Typography variant="body2">
              By default, Microsoft Entra ID access tokens issued to APIs don't include user information like email. 
              Adding these claims to API access tokens ensures your APIs can identify users without additional Graph API calls,
              reducing latency and API dependencies.
            </Typography>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
              📋 What You'll Need
            </Typography>
            <Typography variant="body2">
              • <strong>An API application</strong> registration (where you'll configure the optional claims)<br/>
              • <strong>A client application</strong> registration (that will call your API and receive the access tokens)<br/>
              • <strong>Appropriate API permissions</strong> between the client and API applications
            </Typography>
          </Box>
          
          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure Optional Claims on Your API</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  First, we need to configure the API application to include email claims in access tokens:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to Applications section using the main sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Find and select your API application" 
                      secondary="This is the application that exposes the API endpoints your client will call"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Select' to open the App Tester for the API application" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Add Optional Claims to API Access Tokens</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Configure the API to include email claims in access tokens issued to client applications:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the App Tester sidebar, navigate to 'Token Configuration'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add New Optional Claims'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select 'Access Token' as the token type" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Check the 'email' claim" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Optionally, also add 'upn' (User Principal Name) as a fallback identifier" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add Claim'" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Important Note
                  </Typography>
                  <Typography variant="body2">
                    These optional claims will only appear in access tokens issued <strong>for this API</strong> when 
                    client applications request tokens with this API's scope.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure Required API Permissions</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  For email claims to work properly, the API needs the email permission from Microsoft Graph:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Still in the API app, navigate to 'API Permissions' in the App Tester sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add a permission'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select 'Microsoft Graph'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Choose 'Delegated permissions'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Search for and select 'email' or 'User.Read'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add permissions'" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2 }}>
                  <Chip 
                    label="Admin consent may be required" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Verify API Exposes Scopes (if needed)</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Ensure your API exposes scopes that client applications can request:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the API app, navigate to 'Expose an API'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Verify you have at least one scope defined" 
                      secondary="e.g., 'api://your-api-id/access_as_user' or 'api://your-api-id/read'"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="If no scopes exist, create one:" 
                      secondary="Scope name: 'access_as_user', Display name: 'Access API as user'"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Note down the full scope value (e.g., 'api://12345678-1234-1234-1234-123456789012/access_as_user')" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Note down API's clientId as you'll need it later, unless your client app already has exposed APIs scope configured as Custom API Permission" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Apply Changes to API</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Save all configuration changes to your API application:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Apply Changes' or 'Save'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Wait for the confirmation message" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Switch to Client Application</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Now we need to configure and test with a client application that will call your API:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Go back to Applications section using the main sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Find and select your client application" 
                      secondary="This is a different app registration - the one that will request access tokens for your API"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Select' to open the App Tester for the client application" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Client vs API Applications
                  </Typography>
                  <Typography variant="body2">
                    • <strong>API Application</strong>: Defines what claims are included in access tokens<br/>
                    • <strong>Client Application</strong>: Requests access tokens and receives them with the configured claims
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure Client Permissions to API</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Ensure the client application has permission to call your API:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the client app, navigate to 'API Permissions'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select 'Custom API Permissions'" />
                  </ListItem>              
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add Custome API Permission'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In Add Custom API Permission diallog box, paste API's clientId you previously saved and click 'Find Scopes' button" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select 'Delegated (Scope)'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Check the scope(s) exposed by your API (e.g., 'access_as_user')" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add permissions'" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Apply Changes to Client</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Save the client application configuration:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Apply Changes' or 'Save'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Wait for the confirmation message" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Test Access Token Claims</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Now let's verify the email claim appears in access tokens when the client calls your API:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the client app's App Tester sidebar, select 'Fetch Access Token'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="In the 'Select API Audience' dropdown, choose your API" 
                      secondary="Look for your API's Application ID URI or the scope you noted earlier"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select the appropriate scope format (e.g., 'api://your-api-id/access_as_user')" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Start Authentication'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Complete the authentication flow" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the lower right panel (Active Tokens), find the section for your API" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click the eye icon to view the decoded access token" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Verify the 'email' claim exists and contains the correct value" 
                      secondary="The email should match the authenticated user's email address"
                    />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Expected Token Structure
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {`{
      "aud": "api://your-api-id",
      "email": "user@domain.com",
      "upn": "user@domain.com",
      "scp": "access_as_user",
      ...
    }`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Key Differences: API vs Client Configuration
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="API Application (Step 1-5)" 
                  secondary="Defines what claims are included in access tokens and exposes scopes for client apps" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Client Application (Step 6-9)" 
                  secondary="Requests access tokens for the API and receives tokens with the configured claims" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Token Audience" 
                  secondary="Access tokens are issued for the API (aud claim = API's Application ID URI)" 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Common Issues & Solutions
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Email claim missing from access token" 
                  secondary="Ensure the API (not client) has the optional claims configured and required Graph permissions" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Client can't request API tokens" 
                  secondary="Verify the client has been granted permission to your API's scopes and admin consent if required" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Wrong audience in access token" 
                  secondary="Ensure you're selecting the correct API in the 'Select API Audience' dropdown" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Changes not visible immediately" 
                  secondary="Optional claims changes can take 15-30 minutes to propagate. Try clearing browser cache." 
                />
              </ListItem>
            </List>
          </Box>
          
            <Box sx={{ mt: 4 }}>
                <Button 
                variant="outlined" 
                startIcon={<ArrowRightIcon />}
                onClick={onBack}
                >
                Back to All Scenarios
                </Button>
            </Box>
            </Box>
        );
    };

export default OptionalClaimsApiScenario;